import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import Sparkline from "../../allocation/components/Sparkline";
import { humanEUR, pct, statusTone, hasOwnerProblem } from "./ownershipFormat";
import "../../../components/ui-components/DataTable.css";

// SpendCell merges the amount and its share of total into one right-aligned column — the amount is
// the value, the share its supporting context (no separate progress bar: the composition bar above
// already visualises shares, and a per-row bar hung below the other cells' baseline).
function SpendCell({ amount, share }) {
  return (
    <div className="flex flex-col items-end leading-tight">
      <span className="tabular-nums text-text">{humanEUR(amount)}</span>
      {share != null && <span className="mt-0.5 text-[11px] tabular-nums text-text-3">{pct(share)} of total</span>}
    </div>
  );
}
SpendCell.propTypes = { amount: PropTypes.number, share: PropTypes.number };

// BudgetCell: status-coloured adherence ("72% of cap") with the governing budget's name as a muted
// subline — calmer than a filled pill, and the colour alone carries the signal.
const BUDGET_TONE = { ok: "text-success", attention: "text-warning", critical: "text-danger", neutral: "text-text-2" };
function BudgetCell({ budget }) {
  if (!budget) return <span className="text-xs text-text-3">no budget</span>;
  const tone = statusTone(budget.status);
  return (
    <div className="flex flex-col items-end leading-tight" title="Governing budget (often a parent department) — trailing-30-day spend vs its monthly cap">
      <span className={`text-sm font-medium ${BUDGET_TONE[tone] || "text-text-2"}`}>
        {pct(budget.pctOfCap)} of {budget.capEur > 0 ? `${humanEUR(budget.capEur)} cap` : "cap"}
      </span>
      {budget.budgetName && <span className="mt-0.5 text-[11px] text-text-3">{budget.budgetName}</span>}
    </div>
  );
}
BudgetCell.propTypes = { budget: PropTypes.object };

function TrendCell({ owner }) {
  const t = owner.trend || {};
  const spark = Array.isArray(t.spark) ? t.spark : [];
  if (spark.length < 2) {
    return <span className="text-text-3" title="No trend data for this owner yet">—</span>;
  }
  const d = Number(t.deltaPct) || 0;
  const dPct = Math.round(d * 100);
  // Rising cost is the unfavourable direction; flag a sharp move, keep small ones calm.
  const deltaCls = d > 0.1 ? "text-warning" : d < -0.1 ? "text-success" : "text-text-3";
  return (
    <div className="flex items-center justify-end gap-2" title="Net change (start vs end) over the last 30 days">
      <Sparkline data={spark} goodDirection="down" />
      <span className={`whitespace-nowrap tabular-nums text-xs ${deltaCls}`}>net {dPct > 0 ? "+" : ""}{dPct}%</span>
    </div>
  );
}
TrendCell.propTypes = { owner: PropTypes.object.isRequired };

const ALIGN = { left: "text-left", right: "text-right", center: "text-center" };

const budgetSort = (b) => (b && b.pctOfCap != null ? Number(b.pctOfCap) : -1);

// Columns, rendered header+cell from one definition so alignment can't drift. Owner first, then
// money, the budget governance badge, the trend, and a chevron that makes the row's drill-down
// discoverable. (No "Status" column: it would be budget-status relabelled — duplicating the
// colour-coded Budget chip — and every row here is, by definition, an owner.) cells receive
// (owner, ctx); sortVal/defaultDir drive sorting.
const COLUMNS = [
  {
    key: "owner",
    header: "Owner",
    align: "left",
    sortVal: (o) => (o.ownerName || "").toLowerCase(),
    defaultDir: "asc",
    cell: (o) => (
      <div className="min-w-0">
        {o.path && o.path.length > 0 && (
          <div className="truncate text-[11px] leading-tight text-text-3">{o.path.join(" › ")}</div>
        )}
        <div className="font-medium text-text">{o.ownerName}</div>
      </div>
    ),
  },
  // Spend merges the amount + its share of ALL spend (owned + unowned when grandTotal is supplied;
  // otherwise the backend's pctOfTotal, share of owned only).
  { key: "spend", header: "Spend", align: "right", sortVal: (o) => Number(o.cost) || 0, defaultDir: "desc", cell: (o, ctx) => <SpendCell amount={o.cost} share={ctx.grandTotal ? o.cost / ctx.grandTotal : o.pctOfTotal} /> },
  {
    key: "budget",
    header: "Budget",
    headerTitle: "Budget adherence — trailing-30-day spend measured against the governing budget's monthly cap.",
    align: "right",
    sortVal: (o) => budgetSort(o.budget),
    defaultDir: "desc",
    cell: (o) => <BudgetCell budget={o.budget} />,
  },
  { key: "trend", header: "Trend", align: "right", sortVal: (o) => (o.trend && Number(o.trend.deltaPct)) || 0, defaultDir: "desc", cell: (o) => <TrendCell owner={o} /> },
];

function SortArrow({ active, dir }) {
  if (!active) return null;
  return <span className="ml-1 text-text-2" aria-hidden="true">{dir === "asc" ? "↑" : "↓"}</span>;
}
SortArrow.propTypes = { active: PropTypes.bool, dir: PropTypes.string };

// OwnerHierarchyTable lists the actual owners — the nodes where cost is directly allocated — in a
// self-contained card matching the Financial Control exceptions table. With more than two owners
// the columns become sortable and a "problems only" filter narrows to owners needing attention;
// at ≤2 rows that chrome is suppressed. grandTotal makes "% of total" a share of all spend, and a
// subtle caption reminds the reader of the unowned remainder (so the table reads as complete
// without re-adding the heavy reconciliation footer).
const OwnerHierarchyTable = ({ owners = [], grandTotal = null, unassignedCost = 0 }) => {
  const [sort, setSort] = useState({ key: "spend", dir: "desc" });
  const [problemsOnly, setProblemsOnly] = useState(false);
  const ctx = { grandTotal };

  const [grouped, setGrouped] = useState(false);
  const interactive = owners.length > 2; // sorting + filtering only earn their keep on longer lists
  const problemCount = useMemo(() => owners.filter(hasOwnerProblem).length, [owners]);
  const filtering = interactive && problemsOnly;
  // Group-by-division roll-up (interim, frontend-only): only offered when owners span >1 top-level
  // division (from each owner's org path). The real multi-level tree will come with the backend
  // data-structure redesign.
  const divisionCount = useMemo(() => new Set(owners.map((o) => (o.path && o.path[0]) || "—")).size, [owners]);
  const canGroup = interactive && divisionCount > 1;
  const isGrouped = canGroup && grouped;

  const visible = useMemo(() => {
    const col = COLUMNS.find((c) => c.key === sort.key) || COLUMNS[2];
    const base = filtering ? owners.filter(hasOwnerProblem) : owners;
    if (!interactive) return base; // keep backend spend-ranked order
    const factor = sort.dir === "asc" ? 1 : -1;
    return [...base].sort((a, b) => {
      const va = col.sortVal ? col.sortVal(a) : 0;
      const vb = col.sortVal ? col.sortVal(b) : 0;
      if (va < vb) return -1 * factor;
      if (va > vb) return 1 * factor;
      if ((b.cost || 0) !== (a.cost || 0)) return (b.cost || 0) - (a.cost || 0);
      return (a.ownerNodeId || 0) - (b.ownerNodeId || 0);
    });
  }, [owners, sort, filtering, interactive]);

  // Group the (already sorted/filtered) owners under their top-level division, with a subtotal
  // header per group; groups ordered by spend. Null unless grouping is active.
  const groups = useMemo(() => {
    if (!isGrouped) return null;
    const byDiv = new Map();
    visible.forEach((o) => {
      const key = (o.path && o.path[0]) || "(no division)";
      if (!byDiv.has(key)) byDiv.set(key, []);
      byDiv.get(key).push(o);
    });
    return [...byDiv.entries()]
      .map(([name, members]) => ({ name, members, spend: members.reduce((s, o) => s + (Number(o.cost) || 0), 0) }))
      .sort((a, b) => b.spend - a.spend);
  }, [isGrouped, visible]);

  const onSort = (col) => {
    if (!interactive || !col.sortVal) return;
    setSort((s) => (s.key === col.key ? { key: col.key, dir: s.dir === "asc" ? "desc" : "asc" } : { key: col.key, dir: col.defaultDir || "desc" }));
  };

  // One owner row. `indented` adds left padding when nested under a division group header.
  const ownerRow = (o, indented) => (
    <tr key={o.ownerNodeId}>
      {COLUMNS.map((c) => (
        <td key={c.key} className={`whitespace-nowrap px-4 py-2.5 align-middle ${ALIGN[c.align]} ${c.cls || ""} ${indented && c.key === "owner" ? "pl-10" : ""}`}>
          {c.cell(o, ctx)}
        </td>
      ))}
    </tr>
  );

  return (
    <div className="rounded-card border border-card-bd bg-card">
      {/* Title on the left gives the table an identity; controls sit on the right. */}
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-card-bd px-4 py-3">
        <div className="text-sm font-semibold uppercase tracking-wide text-text-2">Owners</div>
        <div className="flex items-center gap-3">
          {canGroup && (
            <button
              type="button"
              onClick={() => setGrouped((v) => !v)}
              aria-pressed={isGrouped}
              className={`rounded-ctrl border px-2.5 py-1 text-xs font-medium transition-colors ${
                isGrouped ? "border-text bg-text text-white" : "border-card-bd bg-card text-text-2 hover:bg-surface-2"
              }`}
            >
              {isGrouped ? "Ungroup" : "Group by division"}
            </button>
          )}
          {interactive && problemCount > 0 && (
            <button
              type="button"
              onClick={() => setProblemsOnly((v) => !v)}
              aria-pressed={problemsOnly}
              className={`rounded-ctrl border px-2.5 py-1 text-xs font-medium transition-colors ${
                problemsOnly ? "border-warning bg-warning-soft text-warning" : "border-card-bd bg-card text-text-2 hover:bg-surface-2"
              }`}
            >
              {problemsOnly ? "Showing issues only — clear" : `Problems only (${problemCount})`}
            </button>
          )}
          <Link to="/allocation" className="text-xs font-medium text-primary hover:text-primary-hover">
            View allocation →
          </Link>
        </div>
      </div>

      <div className="overflow-x-auto px-2 pb-1">
        <table className="data-table min-w-full">
          <thead>
            <tr>
              {COLUMNS.map((c) => {
                const active = interactive && sort.key === c.key;
                return (
                  <th
                    key={c.key}
                    aria-sort={interactive && c.sortVal ? (active ? (sort.dir === "asc" ? "ascending" : "descending") : "none") : undefined}
                    className="whitespace-nowrap"
                    // Inline: align each header to its column (beats the shared `.data-table th`
                    // text-align:left rule), and let the Owner column absorb the slack so the metric
                    // columns cluster instead of scattering (w-full is a no-op in this repo).
                    style={{ textAlign: c.align, width: c.key === "owner" ? "100%" : undefined }}
                  >
                    {interactive && c.sortVal ? (
                      <button
                        type="button"
                        onClick={() => onSort(c)}
                        title={c.headerTitle}
                        className="inline-flex items-center text-[11px] font-medium uppercase tracking-wide text-text-2 hover:text-text focus:outline-none focus-visible:underline"
                      >
                        <span>{c.header}</span>
                        <SortArrow active={active} dir={sort.dir} />
                      </button>
                    ) : (
                      <span title={c.headerTitle} className="text-[11px] font-medium uppercase tracking-wide text-text-2">{c.header}</span>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {isGrouped
              ? groups.map((g) => (
                  <React.Fragment key={g.name}>
                    <tr className="bg-surface-2 text-text">
                      <td className="text-left font-semibold">{g.name}</td>
                      <td className="text-right font-semibold tabular-nums">{humanEUR(g.spend)}</td>
                      <td colSpan={2} />
                    </tr>
                    {g.members.map((o) => ownerRow(o, true))}
                  </React.Fragment>
                ))
              : visible.map((o) => ownerRow(o, false))}
            {visible.length === 0 && (
              <tr>
                <td colSpan={COLUMNS.length} className="px-4 py-6 text-center text-sm text-text-3">
                  No owners match this filter.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {grandTotal != null && grandTotal > 0 && Math.round(unassignedCost) >= 1 && (
        <div className="border-t border-border px-4 py-2 text-xs text-text-3">
          Plus {humanEUR(unassignedCost)} ({pct(unassignedCost / grandTotal)}) unowned — not attributed to an owner.
        </div>
      )}
    </div>
  );
};

OwnerHierarchyTable.propTypes = {
  owners: PropTypes.array,
  grandTotal: PropTypes.number,
  unassignedCost: PropTypes.number,
};

export default OwnerHierarchyTable;
